import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Recording } from "../../api/types";
import { formatDuration } from "../../utils";
import ActivityHistogram from "./ActivityHistogram";
import { activityHistoryBins, type ActivityBin } from "./activityModel";

const HOUR = 3600000;
const MIN_WINDOW = 6 * HOUR;

export default function ActivityHistory({
  height,
  bins,
  recordings,
}: {
  height?: number;
  bins: ActivityBin[];
  recordings: Recording[];
}) {
  const { t } = useTranslation();
  const domainStart = bins[0]?.start ?? 0;
  const duration = Math.max(HOUR, (bins[bins.length - 1]?.end ?? domainStart) - domainStart);
  const minimumSize = Math.min(MIN_WINDOW, duration);
  const [range, setRange] = useState<{ start: number; size: number } | null>(null);
  const [selected, setSelected] = useState<number>();
  const chart = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; start: number; moved: boolean } | null>(null);
  const size = Math.max(minimumSize, Math.min(range?.size ?? duration, duration));
  const start = Math.max(
    domainStart,
    Math.min(range?.start ?? domainStart, domainStart + duration - size),
  );
  const grouped = useMemo(
    () => activityHistoryBins(recordings, bins, start, start + size),
    [recordings, bins, start, size],
  );
  const visibleSeconds = grouped.bins.reduce((sum, bin) => sum + bin.seconds, 0);
  const reset = () => {
    setRange(null);
    setSelected(undefined);
  };
  useEffect(() => {
    setRange(null);
    setSelected(undefined);
  }, [domainStart, duration]);
  const zoom = useCallback(
    (factor: number, anchor = 0.5) => {
      setRange(current => {
        const size = Math.min(current?.size ?? duration, duration);
        const start = Math.max(
          domainStart,
          Math.min(current?.start ?? domainStart, domainStart + duration - size),
        );
        const roundedSize = Math.round((size * factor) / HOUR) * HOUR;
        // Small trackpad deltas must still move between hourly zoom levels.
        const nextSize = Math.max(
          minimumSize,
          Math.min(
            duration,
            factor > 1 ? Math.max(size + HOUR, roundedSize) : Math.min(size - HOUR, roundedSize),
          ),
        );
        return {
          start: Math.max(
            domainStart,
            Math.min(
              domainStart + duration - nextSize,
              domainStart +
                Math.round((start - domainStart + anchor * (size - nextSize)) / HOUR) * HOUR,
            ),
          ),
          size: nextSize,
        };
      });
    },
    [domainStart, duration, minimumSize],
  );
  // React's delegated wheel listener is passive; cancel native scrolling only for chart gestures.
  useEffect(() => {
    const element = chart.current;
    if (!element) return;
    const wheel = (event: WheelEvent) => {
      const rect = element.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      if (event.metaKey || event.ctrlKey) {
        if (!event.deltaY) return;
        event.preventDefault();
        const anchor = Math.max(0, Math.min(1, (event.clientX - rect.left) / width));
        zoom(Math.exp(Math.max(-100, Math.min(100, event.deltaY)) * 0.01), anchor);
      } else if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey) {
        event.preventDefault();
        setRange(current => {
          const size = Math.max(minimumSize, Math.min(current?.size ?? duration, duration));
          const start = Math.max(
            domainStart,
            Math.min(current?.start ?? domainStart, domainStart + duration - size),
          );
          return {
            start: Math.max(
              domainStart,
              Math.min(
                domainStart + duration - size,
                start + ((event.deltaX || event.deltaY) / width) * size,
              ),
            ),
            size,
          };
        });
      }
    };
    element.addEventListener("wheel", wheel, { passive: false });
    return () => element.removeEventListener("wheel", wheel);
  }, [zoom, domainStart, duration, minimumSize]);
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold">{t("activity.daily")}</h3>
        <p className="text-xs tabular-nums text-text-mid">
          {t("activity.captured_in_view", {
            time: formatDuration(visibleSeconds),
          })}
        </p>
      </div>
      <div
        ref={chart}
        role="region"
        aria-label={t("activity.daily")}
        aria-description={t("activity.chart_gestures")}
        tabIndex={0}
        className="touch-pan-y cursor-grab select-none rounded focus-visible:outline-info active:cursor-grabbing"
        onDoubleClick={reset}
        onKeyDown={event => {
          if (event.key === "+" || event.key === "=") zoom(0.5);
          else if (event.key === "-") zoom(2);
          else if (event.key === "Home" || event.key === "0") reset();
          else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            setRange({
              start: Math.max(
                domainStart,
                Math.min(
                  domainStart + duration - size,
                  start +
                    (event.key === "ArrowLeft" ? -1 : 1) *
                      Math.max(HOUR, Math.round(size / 4 / HOUR) * HOUR),
                ),
              ),
              size,
            });
          } else return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onPointerDown={event => {
          if (event.button !== 0 || event.pointerType === "touch") return;
          drag.current = { x: event.clientX, start, moved: false };
        }}
        onPointerMove={event => {
          if (!(event.buttons & 1)) {
            drag.current = null;
            return;
          }
          const gesture = drag.current;
          if (!gesture) return;
          const distance = event.clientX - gesture.x;
          if (Math.abs(distance) < 4 && !gesture.moved) return;
          gesture.moved = true;
          event.currentTarget.setPointerCapture(event.pointerId);
          const width = event.currentTarget.getBoundingClientRect().width;
          setRange({
            start: Math.max(
              domainStart,
              Math.min(
                domainStart + duration - size,
                gesture.start - Math.round(((distance / Math.max(1, width)) * size) / HOUR) * HOUR,
              ),
            ),
            size,
          });
        }}
        onPointerUp={() => {
          if (drag.current && !drag.current.moved) drag.current = null;
        }}
        onPointerLeave={() => {
          if (!drag.current?.moved) drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        onClickCapture={event => {
          if (drag.current?.moved || event.detail > 1) {
            event.preventDefault();
            event.stopPropagation();
          }
          drag.current = null;
        }}
      >
        <ActivityHistogram
          height={height}
          bins={grouped.bins}
          hourly={grouped.hourly}
          selected={selected}
          onSelect={time => {
            const bin = grouped.bins.find(item => item.start === time)!;
            setSelected(time);
            if (bin.end - bin.start > HOUR) {
              const nextSize = Math.min(duration, Math.max(minimumSize, bin.end - bin.start));
              setRange({
                start: Math.min(bin.start, domainStart + duration - nextSize),
                size: nextSize,
              });
            }
          }}
        />
      </div>
      <div className="mt-3 flex justify-end text-xs text-text-dim">
        <span>
          {t(grouped.hourly ? "activity.bin_hours" : "activity.bin_days", {
            count: grouped.size,
          })}
        </span>
      </div>
    </div>
  );
}
